import FieldContentContainer from '@nile/banyan/src/Containers/PageContainers/FieldContentContainer';
import CustomFieldContext from '@nile/banyan/src/Context/CustomFieldContext';
import {
  customFieldConstraints,
  preFormatForm,
  SaveCard,
  trimInputValues,
  validateForm,
  stopPropagation,
  CUSTOM_FIELDS
} from '@nile/components';
import { plaintextValidator } from '@nile/components/src/Validators/fields';
import { Page, TextStyle } from '@superops/ui-design-system';
import { cloneDeep } from 'lodash';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

const getOptionsError = (options = []) => {
  const errorObj = options[0];
  if (typeof errorObj === 'string') return [errorObj];
  if (
    typeof errorObj === 'object' &&
    Object.values(errorObj.errors)?.[0]?.error?.[0]
  )
    return [Object.values(errorObj.errors)[0].error[0]];
  return [];
};

class CustomFieldSheet extends Component {
  constructor(props) {
    super(props);
    const { intl, field } = props;
    this.state = {
      customField: props.customField || {
        description: '',
        label: '',
        fieldType: props.selectedField?.value || 'TEXT',
        options: [],
        showToClient: false,
        module: props.module
      },
      submitActionLoading: false,
      isDependantFieldType:
        props.selectedField?.value === CUSTOM_FIELDS.depedantField
    };
  }

  updateCustomField = (field, key) => {
    let propertiesToAppend = {};
    const fieldOptions =
      this.state.customField?.options ||
      this.state.customField?.fieldOptions ||
      [];
    if (key === 'options') {
      let updatedFieldOptions = [];
      field.forEach((el, idx) => {
        const currentField = fieldOptions.find(
          (f) =>
            (f.value === el?.value || f.value === el || f?.id === el?.id) &&
            f?.parentOption?.id === el?.parentOption?.id
        );
        const elOrder =
          Object.prototype.hasOwnProperty.call(el, 'order') &&
          el?.order !== undefined
            ? el?.order
            : idx;
        if (currentField) {
          updatedFieldOptions = [
            ...updatedFieldOptions,
            { ...currentField, value: el?.value, order: elOrder }
          ];
        } else {
          updatedFieldOptions = [
            ...updatedFieldOptions,
            {
              ...el,
              id: undefined,
              children: el?.children || null,
              details: el?.details || null,
              order: elOrder
            }
          ];
        }
      });
      propertiesToAppend = {
        fieldOptions: updatedFieldOptions
      };
    }
    this.setState((prevState) => ({
      customField: {
        ...prevState.customField,
        ...propertiesToAppend,
        [key]: field
      }
    }));
  };

  createOrUpdateField = () => {
    const { actions } = this.props;
    let dummyForm = cloneDeep(this.state.customField);
    dummyForm = {
      ...dummyForm,
      options: dummyForm?.options ? dummyForm?.options : null
    };
    const isDependantField = this.state.customField?.parentField;
    const optionMaxLength = this.props.constraints?.optionCharLength;
    const isSingleSelect =
      dummyForm.fieldType === 'RADIO' || dummyForm.fieldType === 'SELECT';
    const optionsLimit = isSingleSelect
      ? this.props.constraints?.singleChoiceOptionsLimit
      : this.props.constraints?.multiChoiceOptionsLimit;
    const formConstraints =
      isDependantField && optionMaxLength
        ? customFieldConstraints(
            dummyForm.fieldType,
            optionsLimit,
            optionMaxLength
          )
        : customFieldConstraints(
            dummyForm.fieldType,
            optionsLimit,
            optionMaxLength
          );

    if (formConstraints.options && optionMaxLength && !isDependantField) {
      formConstraints.options.array = {
        value: {
          length: {
            maximum: optionMaxLength,
            tooLong: `^Option should be of length ${optionMaxLength}`
          }
        }
      };
    }

    if (this.state.customField?.parentEnabled) {
      formConstraints.parentField = plaintextValidator({ mandatory: true });
    }
    const validationResult = validateForm(
      preFormatForm(dummyForm),
      formConstraints
    );

    if (validationResult) {
      this.setState({
        errors: {
          ...validationResult,
          options: getOptionsError(validationResult.options)
        }
      });
      return;
    }
    this.setState({ errors: {}, submitActionLoading: true });
    let trimmedForm = { ...this.state.customField };
    trimmedForm = trimInputValues(trimmedForm);
    if (actions.primaryAction) {
      try {
        actions
          .primaryAction(
            {
              variables: {
                ...trimmedForm,
                options: trimmedForm?.options ? trimmedForm?.options : null
              },
              input: {
                ...trimmedForm,
                options: trimmedForm?.options ? trimmedForm?.options : null
              }
            },
            () => {
              this.setState({ submitActionLoading: false });
              if (actions.close) {
                actions.close();
              }
            }
          )
          .then(() => {
            this.setState({ submitActionLoading: false });
            if (actions.close) {
              actions.close();
            }
          })
          .catch(() => {
            this.setState({ submitActionLoading: false });
          });
      } catch (e) {
        this.setState({ submitActionLoading: false });
      }
    }
  };

  setErrors = (errors) => {
    this.setState({ errors: errors });
  };

  render() {
    const {
      field,
      customField,
      errors,
      submitActionLoading,
      isDependantFieldType
    } = this.state;
    const { intl, actions, hidePreview, module, mode } = this.props;
    const providerValue = {
      value: { ...customField, isDependantFieldType },
      updateCustomField: this.updateCustomField
    };
    return (
      <div onMouseDown={stopPropagation} className="fhp" role="presentation">
        <div className="full-height full-width">
          <CustomFieldContext.Provider value={providerValue}>
            <Page
              fullWidth
              variant="primary"
              contentHeight="100px"
              title={
                <TextStyle weight="Bold">
                  {this.props.customField ? (
                    <FormattedMessage id="admin.CustomFields.create.edit" />
                  ) : (
                    <FormattedMessage id="admin.CustomFields.create.new" />
                  )}
                </TextStyle>
              }
              primaryAction={
                <>
                  <SaveCard
                    handleSave={this.createOrUpdateField.bind(this)}
                    actions={actions}
                    loading={submitActionLoading}
                    isNewLayout
                    disableSave={this.props.disableEdit}
                  />
                </>
              }
            >
              <FieldContentContainer
                page={this.props.page}
                actions={actions}
                hidePreview={hidePreview}
                module={module}
                errors={errors || {}}
                setErrors={this.setErrors}
                constraints={this.props.constraints}
                disableEdit={this.props.disableEdit}
                showWarning={this.props.showWarning}
                mode={mode}
                hideCommonCustomField={this.props.hideCommonCustomField}
                hideMandatoryOnClosure={this.props.hideMandatoryOnClosure}
                hideMandatoryOnCreation={this.props.hideMandatoryOnCreation}
              />
            </Page>
          </CustomFieldContext.Provider>
        </div>
      </div>
    );
  }
}

export default injectIntl(CustomFieldSheet);
